import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useBoundStore } from "../store";
import {
  getMyGreeting,
  getMyWorkspace,
  getWorkspaceBranding,
  WorkspaceBranding,
} from "../services/Workspace";

interface WorkspaceContextValue {
  branding: WorkspaceBranding | null;
  displayName: string | null;
  // Resolved workspace logo URL (light variant), or null.
  logoUrl: string | null;
  // Raw greeting template (may contain {firstName}); null when none configured.
  greetingTemplate: string | null;
}

const WorkspaceContext = createContext<WorkspaceContextValue>({
  branding: null,
  displayName: null,
  logoUrl: null,
  greetingTemplate: null,
});

// A logo key may be an absolute URL (blob storage / CDN, used as-is) or a
// path relative to the frontend's public root (e.g. a static workspace asset
// at frontend/public/workspaces/<slug>/logo-light.png). Same field, both worlds.
const resolveLogoUrl = (key: string | null | undefined): string | null => {
  if (!key) return null;
  if (/^https?:\/\//.test(key)) return key;
  return `/${key.replace(/^\/+/, "")}`;
};

export const useWorkspace = (): WorkspaceContextValue =>
  useContext(WorkspaceContext);

// Preview a workspace's branding on any environment via ?ws=<slug>; kept in
// sessionStorage so it survives client-side navigation and reloads within the
// tab. Complements the real subdomain (pga.queri.law) resolved below.
// See RFC 008 (Workstream B).
const PREVIEW_KEY = "workspacePreviewSlug";

const BASE_DOMAIN = "queri.law";
// Subdomains that are NOT workspaces.
const RESERVED_SUBDOMAINS = new Set([
  "app",
  "www",
  "api",
  "staging",
  "prod",
  "admin",
  "mail",
]);

/**
 * Derive a workspace slug from the host, so pga.queri.law (and the staging
 * stand-in pga-staging.queri.law) theme to that workspace. Returns null for
 * reserved hosts (app/app-staging/staging/…) and non-queri.law hosts (localhost).
 */
const slugFromHostname = (): string | null => {
  const host = window.location.hostname.toLowerCase();
  if (!host.endsWith(`.${BASE_DOMAIN}`)) return null;
  const sub = host.slice(0, host.length - BASE_DOMAIN.length - 1);
  // Only single-label subdomains (e.g. not "a.b.queri.law").
  if (!sub || sub.includes(".")) return null;
  // Strip an environment suffix: pga-staging -> pga; pga (prod) -> pga.
  const slug = sub.replace(/-(staging|prod)$/, "");
  if (!slug || RESERVED_SUBDOMAINS.has(slug)) return null;
  return slug;
};

// Maps branding HSL triplets onto the existing global CSS variables so runtime
// theming needs no per-component changes.
const THEME_VARS: [keyof WorkspaceBranding, string][] = [
  ["primaryHsl", "--primary"],
  ["secondaryHsl", "--secondary"],
  ["accentHsl", "--accent"],
  ["brandHsl", "--brand"],
];

const applyBranding = (branding: WorkspaceBranding | null): void => {
  const root = document.documentElement;
  for (const [field, cssVar] of THEME_VARS) {
    const value = branding?.[field];
    if (value) {
      root.style.setProperty(cssVar, value);
    } else {
      root.style.removeProperty(cssVar);
    }
  }
};

export const WorkspaceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const accessToken = useBoundStore((state) => state.accessToken);
  const [branding, setBranding] = useState<WorkspaceBranding | null>(null);
  const [greetingTemplate, setGreetingTemplate] = useState<string | null>(null);

  // Capture ?ws during the first render — BEFORE a ProtectedRoute redirect to
  // /login (which runs in a child effect) can strip the query string. Reading
  // it inside the effect below would be too late on protected routes.
  const [initialWsParam] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get("ws")
  );
  const [hostSlug] = useState<string | null>(() => slugFromHostname());

  useEffect(() => {
    if (initialWsParam) {
      sessionStorage.setItem(PREVIEW_KEY, initialWsParam);
    }
    // The subdomain (pga.queri.law) or an explicit ?ws preview, whichever
    // applies. Used for logged-out visitors and for non-members previewing.
    const slug =
      initialWsParam || hostSlug || sessionStorage.getItem(PREVIEW_KEY);

    let cancelled = false;

    const resolve = async () => {
      // 1. An authenticated member gets their own workspace — the full
      // experience, including the greeting.
      if (accessToken) {
        const res = await getMyWorkspace();
        if (
          !cancelled &&
          res.success &&
          res.data?.isWorkspace &&
          res.data.branding
        ) {
          setBranding(res.data.branding);
          const greeting = await getMyGreeting();
          if (!cancelled && greeting.success && greeting.data) {
            setGreetingTemplate(greeting.data.template);
          }
          return;
        }
      }

      // 2. Otherwise resolve branding by slug (public) — themes the login page
      // on a subdomain and lets non-members preview.
      if (slug) {
        const res = await getWorkspaceBranding(slug);
        if (!cancelled && res.success && res.data) {
          setBranding(res.data);
          return;
        }
      }

      if (!cancelled) {
        setBranding(null);
        setGreetingTemplate(null);
      }
    };

    resolve();
    return () => {
      cancelled = true;
    };
  }, [accessToken, initialWsParam, hostSlug]);

  useEffect(() => {
    applyBranding(branding);
    return () => applyBranding(null);
  }, [branding]);

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      branding,
      displayName: branding?.displayName ?? null,
      logoUrl: resolveLogoUrl(branding?.logoLightS3Key),
      greetingTemplate,
    }),
    [branding, greetingTemplate]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};
